export class MutagentError extends Error {
  constructor(
    public code: string,
    message: string,
    public suggestion?: string,
    public exitCode = 1
  ) {
    super(message);
    this.name = 'MutagentError';
  }

  toJSON(): { success: false; error: string; code: string; suggestedAction?: string } {
    return {
      success: false,
      error: this.message,
      code: this.code,
      suggestedAction: this.suggestion,
    };
  }
}

export interface AuthenticationErrorOptions {
  suggestions?: string[];
  cause?: Error;
}

export const AUTH_REMEDIATION_MESSAGE = [
  'Authentication required. Options:',
  '  Interactive:     mutagent auth login --browser',
  '  Non-interactive: export MUTAGENT_API_KEY=<your-key>',
  '  CI/CD:           mutagent auth login --api-key <key>',
].join('\n');

export class AuthenticationError extends MutagentError {
  readonly suggestions: string[];
  override readonly cause?: Error;

  constructor(
    message?: string,
    options: AuthenticationErrorOptions = {}
  ) {
    super(
      'AUTH_REQUIRED',
      message ?? 'Authentication required. Please run "mutagent auth login"',
      options.suggestions ? options.suggestions[0] : 'Run: mutagent auth login --browser',
      2
    );
    this.suggestions = options.suggestions ?? [
      'mutagent auth login --browser',
      'export MUTAGENT_API_KEY=<your-key>',
      'mutagent auth login --api-key <key>',
    ];
    this.suggestion = options.suggestions ? options.suggestions[0] : 'Run: mutagent auth login --browser';
    this.cause = options.cause;
  }
}

export class ApiError extends MutagentError {
  readonly statusCode: number;

  constructor(status: number, message: string) {
    super(
      `API_${String(status)}`,
      message,
      status === 401 ? 'Check your API key with: mutagent auth status' : undefined,
      1
    );
    this.statusCode = status;
  }
}

export const WORKSPACE_REMEDIATION_MESSAGE = [
  'Workspace context missing. To fix:',
  '  mutagent config set workspace <workspace-id>   # Set default workspace',
  '  mutagent workspaces list                        # List available workspaces',
].join('\n');

export class WorkspaceContextError extends MutagentError {
  constructor(message?: string) {
    super(
      'WORKSPACE_REQUIRED',
      message ?? 'Workspace context is required but not configured',
      'Run: mutagent config set workspace <workspace-id>',
      3
    );
  }
}

export class ValidationError extends MutagentError {
  constructor(message: string) {
    super(
      'VALIDATION_ERROR',
      message,
      'Check the command syntax with: mutagent <command> --help',
      1
    );
  }
}

// =============================================================================
// Additional Error Classes for Configuration & Auth System
// =============================================================================

export class CredentialsExpiredError extends AuthenticationError {
  readonly expiredAt: Date;

  constructor(expiredAt: Date) {
    super();
    this.expiredAt = expiredAt;
    this.code = 'AUTH_EXPIRED';
    this.message = `Credentials expired at ${expiredAt.toISOString()}`;
    this.suggestion = 'Run "mutagent auth login" to refresh your credentials';
  }
}

export class InvalidApiKeyError extends MutagentError {
  constructor(keyPreview?: string) {
    const preview = keyPreview ? ` (received: "${keyPreview}...")` : '';
    super(
      'INVALID_API_KEY',
      `Invalid API key format${preview}`,
      'API keys should start with "sk_" or "sk-" and be at least 20 characters',
      2
    );
  }
}

export interface ConfigErrorOptions {
  cause?: Error;
  suggestions?: string[];
  configPath?: string;
}

export class ConfigError extends MutagentError {
  readonly configPath?: string;
  readonly suggestions: string[];
  override readonly cause?: Error;

  constructor(
    message: string,
    options: ConfigErrorOptions = {}
  ) {
    super(
      'CONFIG_ERROR',
      message,
      options.suggestions ? options.suggestions[0] : 'Run "mutagent config validate" to check your configuration',
      3
    );
    this.configPath = options.configPath;
    this.suggestions = options.suggestions ?? ['Run "mutagent config validate" to check your configuration'];
    this.suggestion = options.suggestions ? options.suggestions[0] : 'Run "mutagent config validate" to check your configuration';
    this.cause = options.cause;
  }
}

export class ConfigNotFoundError extends ConfigError {
  constructor(configPath: string) {
    super(
      `Configuration file not found: ${configPath}`,
      { suggestions: ['Create a configuration file with "mutagent config init" or use --config'] }
    );
    this.code = 'CONFIG_NOT_FOUND';
    this.suggestion = 'Create a configuration file with "mutagent config init" or use --config';
  }
}

export class ConfigParseError extends ConfigError {
  readonly parseError: Error;

  constructor(
    configPath: string,
    parseError: Error
  ) {
    super(
      `Failed to parse configuration file: ${configPath}`,
      { suggestions: ['Validate your JSON/JavaScript syntax and check for trailing commas'] }
    );
    this.code = 'CONFIG_PARSE_ERROR';
    this.suggestion = 'Validate your JSON/JavaScript syntax and check for trailing commas';
    this.parseError = parseError;
  }
}

export interface PermissionErrorOptions {
  cause?: Error;
}

export class PermissionError extends MutagentError {
  readonly path?: string;
  readonly operation: string;
  override readonly cause?: Error;

  constructor(
    operation: string,
    path?: string,
    options: PermissionErrorOptions = {}
  ) {
    const pathMsg = path ? ` at ${path}` : '';
    super(
      'PERMISSION_ERROR',
      `Permission denied: ${operation}${pathMsg}`,
      'Check file permissions and ensure the file is not read-only',
      8
    );
    this.operation = operation;
    this.path = path;
    this.cause = options.cause;
  }
}

export class NetworkError extends MutagentError {
  readonly url?: string;
  readonly statusCode?: number;

  constructor(
    message = 'Network error',
    url?: string,
    statusCode?: number
  ) {
    super(
      'NETWORK_ERROR',
      message,
      'Check your internet connection and verify the server URL',
      5
    );
    this.url = url;
    this.statusCode = statusCode;
  }
}

export class RateLimitError extends NetworkError {
  readonly retryAfter?: number;

  constructor(
    retryAfter?: number
  ) {
    super(
      'Rate limit exceeded',
      undefined,
      429
    );
    this.code = 'RATE_LIMIT_ERROR';
    this.suggestion = retryAfter 
      ? `Wait ${String(retryAfter)} seconds before retrying`
      : 'Wait a few seconds before retrying';
    this.retryAfter = retryAfter;
    this.exitCode = 6;
  }
}

export class NotFoundError extends MutagentError {
  constructor(resource: string, id: string) {
    super(
      'NOT_FOUND',
      `${resource} not found: ${id}`,
      `Check the ID and try again. List available ${resource.toLowerCase()}s with the appropriate list command.`,
      1
    );
  }
}

export class QuotaExceededError extends MutagentError {
  constructor(message?: string) {
    super(
      'QUOTA_EXCEEDED',
      message ?? 'Quota exceeded',
      'Check your plan limits or contact support to upgrade',
      1
    );
  }
}

export class PrerequisiteMissingError extends MutagentError {
  constructor(what: string, howToFix: string) {
    super(
      'PREREQUISITE_MISSING',
      what,
      howToFix,
      1
    );
  }
}

export class ProviderError extends MutagentError {
  constructor(message: string, suggestion?: string) {
    super(
      'PROVIDER_ERROR',
      message,
      suggestion ?? 'Check your provider configuration with: mutagent providers list',
      1
    );
  }
}

// =============================================================================
// Error Factory Functions
// =============================================================================

export function createErrorFromUnknown(error: unknown): MutagentError {
  if (error instanceof MutagentError) {
    return error;
  }

  if (error instanceof Error) {
    return new MutagentError(
      'UNKNOWN_ERROR',
      error.message,
      undefined,
      1
    );
  }

  return new MutagentError(
    'UNKNOWN_ERROR',
    typeof error === 'string' ? error : 'An unknown error occurred',
    undefined,
    1
  );
}

export function handleError(error: unknown, isJson: boolean): never {
  if (error instanceof MutagentError) {
    if (isJson) {
      const jsonOutput = error.toJSON();
      // Include remediation info for auth errors
      if (error instanceof AuthenticationError) {
        (jsonOutput as Record<string, unknown>).remediation = {
          interactive: 'mutagent auth login --browser',
          nonInteractive: 'export MUTAGENT_API_KEY=<your-key>',
          ciCd: 'mutagent auth login --api-key <key>',
        };
      }
      // Include remediation info for workspace context errors
      if (error instanceof WorkspaceContextError) {
        (jsonOutput as Record<string, unknown>).remediation = {
          setWorkspace: 'mutagent config set workspace <workspace-id>',
          listWorkspaces: 'mutagent workspaces list',
        };
      }
      // Include auth recovery hint for 403 API errors
      if (error instanceof ApiError && error.statusCode === 403) {
        (jsonOutput as Record<string, unknown>).remediation = {
          interactive: 'mutagent auth login --browser',
          nonInteractive: 'export MUTAGENT_API_KEY=<your-key>',
          checkPermissions: 'Verify your API key has the required permissions',
        };
      }
      // Include _agentGuidance for AI agents to self-correct
      if (error.suggestion) {
        const helpMatch = /^Run:\s+(mutagent\s+[^\n]+--help)/.exec(error.suggestion);
        const helpCommand = helpMatch ? helpMatch[1] : 'mutagent --help';
        (jsonOutput as Record<string, unknown>)._agentGuidance = {
          helpCommand,
          suggestion: error.suggestion,
        };
      }
      console.log(JSON.stringify(jsonOutput, null, 2));
    } else {
      console.error(`Error: ${error.message}`);
      if (error instanceof AuthenticationError) {
        console.error('');
        console.error(AUTH_REMEDIATION_MESSAGE);
      } else if (error instanceof WorkspaceContextError) {
        console.error('');
        console.error(WORKSPACE_REMEDIATION_MESSAGE);
      } else if (error instanceof ApiError && error.statusCode === 403) {
        console.error('');
        console.error('Access denied. To fix:');
        console.error('  mutagent auth login --browser    # Re-authenticate');
        console.error('  export MUTAGENT_API_KEY=mt_xxx   # Or use a different API key');
      } else if (error instanceof ValidationError) {
        if (error.suggestion) {
          console.error(`Suggestion: ${error.suggestion}`);
        }
        console.error('');
        console.error('Run `mutagent <command> --help` for usage information.');
      } else if (error.suggestion) {
        console.error(`Suggestion: ${error.suggestion}`);
      }
      // Show help hint for missing arguments or invalid input errors
      if (error.code === 'MISSING_ARGUMENTS' || error.code === 'INVALID_ARGUMENTS' || error.code === 'INVALID_INPUT') {
        console.error('');
        console.error('Run `mutagent <command> --help` for usage information.');
      }
    }
    process.exit(error.exitCode);
  }

  // Catch Speakeasy SDK internal errors that leaked past sdk-client wrapper
  if (error && typeof error === 'object') {
    const sdkErr = error as { name?: string; rawValue?: unknown; rawMessage?: string; message?: string };
    if (
      sdkErr.name === 'SDKValidationError' ||
      sdkErr.name === 'SDKError' ||
      sdkErr.rawValue !== undefined ||
      sdkErr.rawMessage !== undefined
    ) {
      const friendlyMessage = sdkErr.rawMessage ?? sdkErr.message ?? 'Request failed';
      const wrapped = new MutagentError(
        'REQUEST_FAILED',
        friendlyMessage,
        'Check your input and try again. Run the command with --help for usage details.'
      );
      if (isJson) {
        console.log(JSON.stringify(wrapped.toJSON(), null, 2));
      } else {
        console.error(`Error: ${wrapped.message}`);
        if (wrapped.suggestion) {
          console.error(`Suggestion: ${wrapped.suggestion}`);
        }
      }
      process.exit(1);
    }
  }

  // Unknown error
  const message = error instanceof Error ? error.message : 'Unknown error';
  if (isJson) {
    console.log(JSON.stringify({ success: false, error: message, code: 'UNKNOWN_ERROR' }, null, 2));
  } else {
    console.error(`Error: ${message}`);
  }
  process.exit(1);
}
